<script>
import { mapGetters } from 'vuex';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import { useAlert } from 'dashboard/composables';
import googleCalendarAPI from '../../../api/googleCalendar';
import calendarAPI from '../../../api/calendar';

export default {
  components: {
    FullCalendar,
  },
  data() {
    return {
      currentTitle: '',
      currentView: 'dayGridMonth',
      showEventModal: false,
      isNewEvent: false,
      showSidebar: true,
      showViewDropdown: false,
      miniCalendarDate: new Date(),
      eventForm: {
        title: '',
        start: '',
        end: '',
        description: '',
        syncWithGoogle: true,
      },
      selectedEvent: null,
      isGoogleConnected: false,
      isSyncing: false,
      searchQuery: '',
      myCalendars: [
        { id: 'scheduled', name: 'Agendados', color: '#039be5', checked: true },
        { id: 'confirmed', name: 'Confirmados', color: '#0b8043', checked: true },
        { id: 'tasks', name: 'Tarefas', color: '#8e24aa', checked: true },
        { id: 'completed', name: 'Concluídos', color: '#616161', checked: true },
      ],
      myCalendarsExpanded: true,
      otherCalendarsExpanded: false,
      googleEmail: null,
      googleCalendars: [],
      selectedAgent: '',
      selectedType: '',
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
        initialView: 'dayGridMonth',
        locale: ptBrLocale,
        headerToolbar: false,
        height: '100%',
        editable: true,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: 3,
        weekends: true,
        events: this.fetchEvents,
        select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        eventDrop: this.handleEventDrop,
        eventResize: this.handleEventResize,
        datesSet: this.handleDatesSet,
        slotMinTime: '06:00:00',
        slotMaxTime: '22:00:00',
        nowIndicator: true,
        navLinks: true,
        fixedWeekCount: false,
        showNonCurrentDates: true,
        firstDay: 0,
        businessHours: {
          daysOfWeek: [1, 2, 3, 4, 5],
          startTime: '09:00',
          endTime: '18:00',
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
    }),
    calendarApi() {
      return this.$refs.fullCalendar?.getApi();
    },
    viewOptions() {
      return [
        { label: 'Dia', value: 'timeGridDay' },
        { label: 'Semana', value: 'timeGridWeek' },
        { label: 'Mês', value: 'dayGridMonth' },
        { label: 'Ano', value: 'dayGridYear' },
        { label: 'Agenda', value: 'listWeek' },
      ];
    },
    currentViewLabel() {
      const view = this.viewOptions.find(v => v.value === this.currentView);
      return view ? view.label : 'Mês';
    },
    miniCalendarWeeks() {
      const weeks = [];
      const year = this.miniCalendarDate.getFullYear();
      const month = this.miniCalendarDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - startDate.getDay());

      for (let w = 0; w < 6; w++) {
        const week = [];
        for (let d = 0; d < 7; d++) {
          const date = new Date(startDate);
          date.setDate(startDate.getDate() + w * 7 + d);
          week.push({
            date: date.getDate(),
            fullDate: new Date(date),
            isCurrentMonth: date.getMonth() === month,
            isToday: this.isToday(date),
          });
        }
        weeks.push(week);
        if (week[6].fullDate > lastDay && week[6].fullDate.getMonth() !== month) break;
      }
      return weeks;
    },
    miniCalendarTitle() {
      const months = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ];
      return `${months[this.miniCalendarDate.getMonth()]} de ${this.miniCalendarDate.getFullYear()}`;
    },
  },
  mounted() {
    this.loadGoogleCalendarStatus();
    this.loadAgents();
  },
  methods: {
    isToday(date) {
      const today = new Date();
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    },

    async fetchEvents(fetchInfo, successCallback) {
      try {
        const params = {
          start: fetchInfo.startStr,
          end: fetchInfo.endStr,
          type: this.selectedType,
          agent_id: this.selectedAgent,
        };

        const { data } = await calendarAPI.getEvents(params);
        successCallback(data || []);
      } catch {
        successCallback([]);
      }
    },

    async loadGoogleCalendarStatus() {
      try {
        const { data } = await googleCalendarAPI.getStatus();
        this.isGoogleConnected = data.connected;
        this.googleEmail = data.email;

        if (this.isGoogleConnected) {
          await this.loadGoogleCalendars();
        }
      } catch {
        // Silently fail
      }
    },

    async loadGoogleCalendars() {
      try {
        const { data } = await googleCalendarAPI.listCalendars();
        this.googleCalendars = data.calendars.map(cal => ({
          ...cal,
          visible: false,
        }));
      } catch {
        // Silently fail
      }
    },

    async loadAgents() {
      try {
        await this.$store.dispatch('agents/get');
      } catch {
        // Silently fail
      }
    },

    refreshCalendar() {
      this.calendarApi?.refetchEvents();
    },

    previousPeriod() {
      this.calendarApi?.prev();
    },

    nextPeriod() {
      this.calendarApi?.next();
    },

    goToToday() {
      this.calendarApi?.today();
      this.miniCalendarDate = new Date();
    },

    changeView(viewType) {
      this.calendarApi?.changeView(viewType);
      this.currentView = viewType;
      this.showViewDropdown = false;
    },

    handleDatesSet(dateInfo) {
      this.currentTitle = dateInfo.view.title;
      this.currentView = dateInfo.view.type;
    },

    handleDateSelect(selectInfo) {
      this.isNewEvent = true;
      this.eventForm = {
        title: '',
        start: this.formatDateTimeLocal(selectInfo.start),
        end: this.formatDateTimeLocal(selectInfo.end),
        description: '',
        syncWithGoogle: this.isGoogleConnected,
      };
      this.selectedEvent = selectInfo;
      this.showEventModal = true;
      this.calendarApi?.unselect();
    },

    handleEventClick(clickInfo) {
      const event = clickInfo.event;

      this.isNewEvent = false;
      this.eventForm = {
        id: event.id,
        title: event.title,
        start: this.formatDateTimeLocal(event.start),
        end: this.formatDateTimeLocal(event.end),
        description: event.extendedProps.description || '',
        syncWithGoogle: event.extendedProps.synced_with_google || false,
      };
      this.selectedEvent = event;
      this.showEventModal = true;
    },

    async handleEventDrop(dropInfo) {
      try {
        useAlert('Evento atualizado');
      } catch {
        dropInfo.revert();
        useAlert('Falha ao atualizar');
      }
    },

    async handleEventResize(resizeInfo) {
      try {
        useAlert('Evento atualizado');
      } catch {
        resizeInfo.revert();
        useAlert('Falha ao atualizar');
      }
    },

    createNewEvent() {
      this.isNewEvent = true;
      this.eventForm = {
        title: '',
        start: this.formatDateTimeLocal(new Date()),
        end: this.formatDateTimeLocal(new Date(Date.now() + 3600000)),
        description: '',
        syncWithGoogle: this.isGoogleConnected,
      };
      this.showEventModal = true;
    },

    async saveEvent() {
      useAlert('Evento salvo');
      this.closeEventModal();
      this.refreshCalendar();
    },

    async deleteEvent() {
      useAlert('Evento excluído');
      this.closeEventModal();
      this.refreshCalendar();
    },

    closeEventModal() {
      this.showEventModal = false;
      this.selectedEvent = null;
      this.eventForm = {
        title: '',
        start: '',
        end: '',
        description: '',
        syncWithGoogle: true,
      };
    },

    async syncWithGoogle() {
      this.isSyncing = true;
      try {
        await googleCalendarAPI.sync();
        useAlert('Sincronizado com sucesso');
        this.refreshCalendar();
      } catch {
        useAlert('Falha na sincronização');
      } finally {
        this.isSyncing = false;
      }
    },

    goToIntegrations() {
      this.$router.push({
        name: 'settings_integrations_google_calendar',
      });
    },

    goBackToKanban() {
      this.$router.push({ name: 'kanban_view' });
    },

    formatDateTimeLocal(date) {
      if (!date) return '';
      const d = new Date(date);
      const offset = d.getTimezoneOffset() * 60000;
      const localDate = new Date(d.getTime() - offset);
      return localDate.toISOString().slice(0, 16);
    },

    toggleSidebar() {
      this.showSidebar = !this.showSidebar;
    },

    miniCalendarPrev() {
      const d = new Date(this.miniCalendarDate);
      d.setMonth(d.getMonth() - 1);
      this.miniCalendarDate = d;
    },

    miniCalendarNext() {
      const d = new Date(this.miniCalendarDate);
      d.setMonth(d.getMonth() + 1);
      this.miniCalendarDate = d;
    },

    selectMiniCalendarDate(day) {
      this.calendarApi?.gotoDate(day.fullDate);
    },
  },
};
</script>

<template>
  <section class="gcal-page">
    <!-- Header - Google Calendar Style -->
    <header class="gcal-header">
      <!-- Left Section -->
      <div class="gcal-header-left">
        <!-- Botão Voltar para Kanban -->
        <router-link
          :to="{ name: 'kanban_view' }"
          class="gcal-back-btn"
          title="Voltar para o Kanban"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </router-link>

        <!-- Hamburger Menu -->
        <button class="gcal-icon-btn" title="Menu principal" @click="toggleSidebar">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
          </svg>
        </button>

        <!-- Calendar Logo -->
        <div class="gcal-logo" @click="goBackToKanban">
          <div class="gcal-logo-icon">
            <span class="gcal-logo-day">{{ new Date().getDate() }}</span>
          </div>
          <span class="gcal-logo-text">Agenda</span>
        </div>

        <!-- Today Button -->
        <button class="gcal-today-btn" @click="goToToday">
          Hoje
        </button>

        <!-- Navigation Arrows -->
        <button class="gcal-nav-btn" @click="previousPeriod">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </button>
        <button class="gcal-nav-btn" @click="nextPeriod">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </button>

        <!-- Current Period Title -->
        <h1 class="gcal-title">{{ currentTitle }}</h1>
      </div>

      <!-- Right Section -->
      <div class="gcal-header-right">
        <!-- Search (hidden for now) -->
        <!-- <button class="gcal-icon-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
        </button> -->

        <!-- View Dropdown -->
        <div class="gcal-view-dropdown">
          <button class="gcal-view-btn" @click="showViewDropdown = !showViewDropdown">
            {{ currentViewLabel }}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </button>
          <div v-if="showViewDropdown" class="gcal-dropdown-menu" @click.stop>
            <button
              v-for="view in viewOptions"
              :key="view.value"
              class="gcal-dropdown-item"
              :class="{ active: currentView === view.value }"
              @click="changeView(view.value)"
            >
              {{ view.label }}
            </button>
          </div>
        </div>

        <!-- Settings -->
        <button class="gcal-icon-btn" title="Configurações" @click="goToIntegrations">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"
            />
          </svg>
        </button>
      </div>
    </header>

    <!-- Main Content -->
    <div class="gcal-main">
      <!-- Sidebar -->
      <aside v-show="showSidebar" class="gcal-sidebar">
        <!-- Create Button -->
        <button class="gcal-create-btn" @click="createNewEvent">
          <svg width="36" height="36" viewBox="0 0 36 36">
            <path fill="#34a853" d="M16 16v14h4V16h14v-4H20V-2h-4v14H-2v4h14z" />
          </svg>
          <span>Criar</span>
        </button>

        <!-- Mini Calendar -->
        <div class="gcal-mini-calendar">
          <div class="gcal-mini-header">
            <span class="gcal-mini-title">{{ miniCalendarTitle }}</span>
            <div class="gcal-mini-nav">
              <button class="gcal-mini-nav-btn" @click="miniCalendarPrev">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </button>
              <button class="gcal-mini-nav-btn" @click="miniCalendarNext">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
              </button>
            </div>
          </div>
          <div class="gcal-mini-grid">
            <div class="gcal-mini-weekdays">
              <span v-for="day in ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']" :key="day" class="gcal-mini-weekday">{{ day }}</span>
            </div>
            <div v-for="(week, wi) in miniCalendarWeeks" :key="wi" class="gcal-mini-week">
              <button
                v-for="(day, di) in week"
                :key="di"
                class="gcal-mini-day"
                :class="{
                  'other-month': !day.isCurrentMonth,
                  'is-today': day.isToday,
                }"
                @click="selectMiniCalendarDate(day)"
              >
                {{ day.date }}
              </button>
            </div>
          </div>
        </div>

        <!-- Search People -->
        <div class="gcal-search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="gcal-search-icon">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Pesquisar pessoas"
            class="gcal-search-input no-margin"
          />
        </div>

        <!-- My Calendars Section -->
        <div class="gcal-calendar-section">
          <button class="gcal-section-header" @click="myCalendarsExpanded = !myCalendarsExpanded">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="gcal-expand-icon"
              :class="{ expanded: myCalendarsExpanded }"
            >
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
            </svg>
            <span>Minhas agendas</span>
          </button>
          <div v-show="myCalendarsExpanded" class="gcal-calendar-list">
            <label
              v-for="cal in myCalendars"
              :key="cal.id"
              class="gcal-calendar-item"
            >
              <input
                v-model="cal.checked"
                type="checkbox"
                class="gcal-checkbox"
                :style="{ '--checkbox-color': cal.color }"
                @change="refreshCalendar"
              />
              <span class="gcal-calendar-name">{{ cal.name }}</span>
            </label>
          </div>
        </div>

        <!-- Other Calendars Section - Only show when Google is connected -->
        <div v-if="isGoogleConnected && googleCalendars.length > 0" class="gcal-calendar-section">
          <div class="gcal-section-header-with-actions">
            <button class="gcal-section-header" @click="otherCalendarsExpanded = !otherCalendarsExpanded">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="gcal-expand-icon"
                :class="{ expanded: otherCalendarsExpanded }"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
              <span>Outras agendas</span>
            </button>
            <button class="gcal-add-btn" title="Adicionar calendário" @click="goToIntegrations">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
            </button>
          </div>
          <div v-show="otherCalendarsExpanded" class="gcal-calendar-list">
            <label
              v-for="cal in googleCalendars"
              :key="cal.id"
              class="gcal-calendar-item"
            >
              <input
                v-model="cal.visible"
                type="checkbox"
                class="gcal-checkbox"
                :style="{ '--checkbox-color': cal.backgroundColor || '#4285f4' }"
                @change="refreshCalendar"
              />
              <span class="gcal-calendar-name">{{ cal.summary || cal.name }}</span>
            </label>
          </div>
        </div>

        <!-- Connect Google Calendar - Show when not connected -->
        <div v-if="!isGoogleConnected" class="gcal-connect-google">
          <button class="gcal-connect-btn" @click="goToIntegrations">
            <svg class="gcal-google-icon" viewBox="0 0 24 24" width="18" height="18">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <span>Conectar Google Calendar</span>
          </button>
        </div>
      </aside>

      <!-- Calendar Grid -->
      <div class="gcal-calendar-container">
        <FullCalendar
          ref="fullCalendar"
          class="gcal-fullcalendar"
          :options="calendarOptions"
        />
      </div>
    </div>

    <!-- Event Modal -->
    <div v-if="showEventModal" class="gcal-modal-overlay" @click.self="closeEventModal">
      <div class="gcal-modal">
        <div class="gcal-modal-header">
          <input
            v-model="eventForm.title"
            type="text"
            class="gcal-modal-title-input"
            placeholder="Adicionar título"
          />
          <button class="gcal-modal-close" @click="closeEventModal">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        <div class="gcal-modal-body">
          <div class="gcal-modal-row">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="gcal-modal-icon">
              <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
              <path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
            </svg>
            <div class="gcal-modal-datetime">
              <input v-model="eventForm.start" type="datetime-local" class="gcal-datetime-input" />
              <span class="gcal-datetime-separator">—</span>
              <input v-model="eventForm.end" type="datetime-local" class="gcal-datetime-input" />
            </div>
          </div>

          <div class="gcal-modal-row">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="gcal-modal-icon">
              <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9h-4v4h-2v-4H9V9h4V5h2v4h4v2z" />
            </svg>
            <textarea
              v-model="eventForm.description"
              class="gcal-modal-textarea"
              placeholder="Adicionar descrição"
              rows="3"
            />
          </div>
        </div>

        <div class="gcal-modal-footer">
          <button v-if="!isNewEvent" class="gcal-modal-delete" @click="deleteEvent">
            Excluir
          </button>
          <div class="gcal-modal-actions">
            <button class="gcal-modal-cancel" @click="closeEventModal">
              Cancelar
            </button>
            <button class="gcal-modal-save" @click="saveEvent">
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Click outside handler for dropdown -->
    <div
      v-if="showViewDropdown"
      class="gcal-dropdown-backdrop"
      @click="showViewDropdown = false"
    />
  </section>
</template>

<style lang="scss">
// Google Calendar Colors
$gcal-primary: #1a73e8;
$gcal-primary-hover: #1557b0;
$gcal-bg: #ffffff;
$gcal-bg-dark: #202124;
$gcal-sidebar-bg: #ffffff;
$gcal-sidebar-bg-dark: #202124;
$gcal-border: #dadce0;
$gcal-border-dark: #5f6368;
$gcal-text: #3c4043;
$gcal-text-dark: #e8eaed;
$gcal-text-secondary: #5f6368;
$gcal-text-secondary-dark: #9aa0a6;
$gcal-hover: #f1f3f4;
$gcal-hover-dark: #3c4043;

.gcal-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: $gcal-bg;
  font-family: 'Google Sans', Roboto, Arial, sans-serif;

  .dark & {
    background: $gcal-bg-dark;
  }
}

// Header
.gcal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 8px 16px;
  border-bottom: 1px solid $gcal-border;
  background: $gcal-bg;

  .dark & {
    background: $gcal-bg-dark;
    border-color: $gcal-border-dark;
  }
}

.gcal-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.gcal-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.gcal-icon-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $gcal-text-secondary;
  transition: background 0.2s;

  &:hover {
    background: $gcal-hover;
  }

  .dark & {
    color: $gcal-text-secondary-dark;

    &:hover {
      background: $gcal-hover-dark;
    }
  }
}

.gcal-back-btn {
  width: 36px;
  height: 36px;
  border: 1px solid $gcal-border;
  background: transparent;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $gcal-text-secondary;
  transition: all 0.2s;
  text-decoration: none;

  &:hover {
    background: $gcal-hover;
    color: $gcal-text;
    border-color: darken($gcal-border, 10%);
  }

  .dark & {
    color: $gcal-text-secondary-dark;
    border-color: $gcal-border-dark;

    &:hover {
      background: $gcal-hover-dark;
      color: $gcal-text-dark;
      border-color: lighten($gcal-border-dark, 10%);
    }
  }
}

.gcal-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  cursor: pointer;
}

.gcal-logo-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #4285f4 25%, #ea4335 25%, #ea4335 50%, #fbbc05 50%, #fbbc05 75%, #34a853 75%);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 4px;
    background: white;
    border-radius: 2px;
  }
}

.gcal-logo-day {
  position: relative;
  z-index: 1;
  font-size: 18px;
  font-weight: 500;
  color: #70757a;
}

.gcal-logo-text {
  font-size: 22px;
  color: $gcal-text-secondary;

  .dark & {
    color: $gcal-text-secondary-dark;
  }
}

.gcal-today-btn {
  height: 36px;
  padding: 0 24px;
  margin-left: 16px;
  border: 1px solid $gcal-border;
  background: transparent;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  color: $gcal-text;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: $gcal-hover;
  }

  .dark & {
    border-color: $gcal-border-dark;
    color: $gcal-text-dark;

    &:hover {
      background: $gcal-hover-dark;
    }
  }
}

.gcal-nav-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $gcal-text-secondary;
  transition: background 0.2s;

  &:hover {
    background: $gcal-hover;
  }

  .dark & {
    color: $gcal-text-secondary-dark;

    &:hover {
      background: $gcal-hover-dark;
    }
  }
}

.gcal-title {
  font-size: 22px;
  font-weight: 400;
  color: $gcal-text;
  margin-left: 8px;
  text-transform: capitalize;

  .dark & {
    color: $gcal-text-dark;
  }
}

// View Dropdown
.gcal-view-dropdown {
  position: relative;
}

.gcal-view-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 8px 0 16px;
  border: 1px solid $gcal-border;
  background: transparent;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  color: $gcal-text;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: $gcal-hover;
  }

  .dark & {
    border-color: $gcal-border-dark;
    color: $gcal-text-dark;

    &:hover {
      background: $gcal-hover-dark;
    }
  }
}

.gcal-dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  min-width: 120px;
  background: $gcal-bg;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 100;
  overflow: hidden;

  .dark & {
    background: $gcal-bg-dark;
  }
}

.gcal-dropdown-item {
  width: 100%;
  padding: 8px 16px;
  border: none;
  background: transparent;
  text-align: left;
  font-size: 14px;
  color: $gcal-text;
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: $gcal-hover;
  }

  &.active {
    background: #e8f0fe;
    color: $gcal-primary;
  }

  .dark & {
    color: $gcal-text-dark;

    &:hover {
      background: $gcal-hover-dark;
    }

    &.active {
      background: #394457;
    }
  }
}

.gcal-dropdown-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99;
}

// Main Layout
.gcal-main {
  display: flex;
  flex: 1;
  overflow: hidden;
}

// Sidebar
.gcal-sidebar {
  width: 220px;
  flex-shrink: 0;
  background: $gcal-sidebar-bg;
  border-right: 1px solid $gcal-border;
  overflow-y: auto;
  padding: 12px 8px;

  .dark & {
    background: $gcal-sidebar-bg-dark;
    border-color: $gcal-border-dark;
  }
}

.gcal-create-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 16px 2px 6px;
  border: none;
  background: transparent;
  border-radius: 24px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: box-shadow 0.2s;
  margin-bottom: 16px;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16), 0 2px 4px rgba(0, 0, 0, 0.24);
  }

  svg {
    width: 28px;
    height: 28px;
  }

  span {
    font-size: 14px;
    font-weight: 500;
    color: $gcal-text;

    .dark & {
      color: $gcal-text-dark;
    }
  }
}

// Mini Calendar
.gcal-mini-calendar {
  margin-bottom: 16px;
}

.gcal-mini-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 4px;
}

.gcal-mini-title {
  font-size: 14px;
  font-weight: 500;
  color: $gcal-text;

  .dark & {
    color: $gcal-text-dark;
  }
}

.gcal-mini-nav {
  display: flex;
  gap: 4px;
}

.gcal-mini-nav-btn {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $gcal-text-secondary;

  &:hover {
    background: $gcal-hover;
  }

  .dark & {
    color: $gcal-text-secondary-dark;

    &:hover {
      background: $gcal-hover-dark;
    }
  }
}

.gcal-mini-grid {
  font-size: 10px;
}

.gcal-mini-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  margin-bottom: 4px;
}

.gcal-mini-weekday {
  padding: 4px;
  font-size: 10px;
  font-weight: 500;
  color: $gcal-text-secondary;

  .dark & {
    color: $gcal-text-secondary-dark;
  }
}

.gcal-mini-week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.gcal-mini-day {
  width: 24px;
  height: 24px;
  margin: 0 auto;
  border: none;
  background: transparent;
  border-radius: 50%;
  font-size: 10px;
  color: $gcal-text;
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: $gcal-hover;
  }

  &.other-month {
    color: $gcal-text-secondary;
  }

  &.is-today {
    background: $gcal-primary;
    color: white;

    &:hover {
      background: $gcal-primary-hover;
    }
  }

  .dark & {
    color: $gcal-text-dark;

    &:hover {
      background: $gcal-hover-dark;
    }

    &.other-month {
      color: $gcal-text-secondary-dark;
    }
  }
}

// Search Box
.gcal-search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  margin-bottom: 12px;
  border: 1px solid $gcal-border;
  border-radius: 8px;
  transition: box-shadow 0.2s;

  &:focus-within {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  }

  .dark & {
    border-color: $gcal-border-dark;
  }
}

.gcal-search-icon {
  color: $gcal-text-secondary;
  flex-shrink: 0;

  .dark & {
    color: $gcal-text-secondary-dark;
  }
}

.gcal-search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  color: $gcal-text;
  outline: none;
  margin-bottom: 0;

  &::placeholder {
    color: $gcal-text-secondary;
  }

  .dark & {
    color: $gcal-text-dark;

    &::placeholder {
      color: $gcal-text-secondary-dark;
    }
  }
}

// Calendar Sections
.gcal-calendar-section {
  margin-bottom: 8px;
}

.gcal-section-header-with-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.gcal-section-header {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: $gcal-text;

  .dark & {
    color: $gcal-text-dark;
  }
}

.gcal-expand-icon {
  transition: transform 0.2s;
  color: $gcal-text-secondary;

  &.expanded {
    transform: rotate(90deg);
  }

  .dark & {
    color: $gcal-text-secondary-dark;
  }
}

.gcal-add-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $gcal-text-secondary;

  &:hover {
    background: $gcal-hover;
  }

  .dark & {
    color: $gcal-text-secondary-dark;

    &:hover {
      background: $gcal-hover-dark;
    }
  }
}

.gcal-calendar-list {
  padding-left: 20px;
}

.gcal-calendar-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: $gcal-hover;
  }

  .dark &:hover {
    background: $gcal-hover-dark;
  }
}

.gcal-checkbox {
  --checkbox-color: #1a73e8;
  width: 16px;
  height: 16px;
  border-radius: 2px;
  border: 2px solid var(--checkbox-color);
  background: transparent;
  cursor: pointer;
  appearance: none;
  position: relative;
  flex-shrink: 0;

  &:checked {
    background: var(--checkbox-color);

    &::after {
      content: '';
      position: absolute;
      left: 4px;
      top: 0px;
      width: 4px;
      height: 9px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
}

.gcal-calendar-name {
  font-size: 12px;
  color: $gcal-text;

  .dark & {
    color: $gcal-text-dark;
  }
}

// Connect Google Calendar Button
.gcal-connect-google {
  padding: 12px 8px;
  margin-top: 8px;
}

.gcal-connect-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid $gcal-primary;
  background: rgba(26, 115, 232, 0.08);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: rgba(26, 115, 232, 0.15);
    border-color: $gcal-primary-hover;
  }

  .dark & {
    border-color: $gcal-primary;
    background: rgba(26, 115, 232, 0.12);

    &:hover {
      background: rgba(26, 115, 232, 0.2);
    }
  }

  span {
    font-size: 13px;
    font-weight: 500;
    color: $gcal-text;

    .dark & {
      color: $gcal-text-dark;
    }
  }
}

.gcal-google-icon {
  flex-shrink: 0;
}

// Calendar Container - with padding around calendar
.gcal-calendar-container {
  flex: 1;
  overflow: hidden;
  background: #f8f9fa;
  padding: 16px;

  .dark & {
    background: #202124;
  }
}

.gcal-fullcalendar {
  height: 100%;
  width: 100%;
}

// FullCalendar Base Styles
.gcal-calendar-container .fc {
  --fc-border-color: #3c4043;
  --fc-today-bg-color: transparent;
  --fc-event-bg-color: #{$gcal-primary};
  --fc-event-border-color: #{$gcal-primary};
  --fc-page-bg-color: #202124;
  --fc-neutral-bg-color: #202124;
  font-family: 'Google Sans', Roboto, Arial, sans-serif;
  background: #202124;
  border-radius: 8px;
  overflow: hidden;
}

// Light mode overrides
body:not(.dark) .gcal-calendar-container .fc {
  --fc-border-color: #dadce0;
  --fc-page-bg-color: #ffffff;
  --fc-neutral-bg-color: #ffffff;
  background: #ffffff;
}

// Scrollgrid - main calendar structure
.gcal-calendar-container .fc-scrollgrid {
  border: none !important;
}

// All cells get proper borders
.gcal-calendar-container .fc-scrollgrid td,
.gcal-calendar-container .fc-scrollgrid th {
  border: 1px solid #3c4043 !important;
}

body:not(.dark) .gcal-calendar-container .fc-scrollgrid td,
body:not(.dark) .gcal-calendar-container .fc-scrollgrid th {
  border-color: #dadce0 !important;
}

// Column Headers (DOM, SEG, TER, etc.)
.gcal-calendar-container .fc-col-header {
  background: #202124;
}

body:not(.dark) .gcal-calendar-container .fc-col-header {
  background: #ffffff;
}

.gcal-calendar-container .fc-col-header-cell {
  padding: 12px 0;
  background: transparent;
}

.gcal-calendar-container .fc-col-header-cell-cushion {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  color: #9aa0a6;
  letter-spacing: 0.3px;
  padding: 8px 0;
}

body:not(.dark) .gcal-calendar-container .fc-col-header-cell-cushion {
  color: #70757a;
}

// Day Grid Cells
.gcal-calendar-container .fc-daygrid-day {
  min-height: 100px;
  background: #202124;
}

body:not(.dark) .gcal-calendar-container .fc-daygrid-day {
  background: #ffffff;
}

// Other month days - slightly different
.gcal-calendar-container .fc-day-other {
  background: #171717 !important;
}

body:not(.dark) .gcal-calendar-container .fc-day-other {
  background: #f8f9fa !important;
}

.gcal-calendar-container .fc-daygrid-day-frame {
  padding: 4px;
  min-height: 100%;
}

// Day number positioning - top left
.gcal-calendar-container .fc-daygrid-day-top {
  flex-direction: row;
  justify-content: flex-start;
  padding: 4px 8px;
}

.gcal-calendar-container .fc-daygrid-day-number {
  font-size: 12px;
  font-weight: 500;
  color: #e8eaed;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  padding: 0;
  text-decoration: none;
}

body:not(.dark) .gcal-calendar-container .fc-daygrid-day-number {
  color: #3c4043;
}

// Today's date - blue circle
.gcal-calendar-container .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
  background: $gcal-primary;
  color: white !important;
}

// Other month day numbers
.gcal-calendar-container .fc-day-other .fc-daygrid-day-number {
  color: #5f6368;
}

body:not(.dark) .gcal-calendar-container .fc-day-other .fc-daygrid-day-number {
  color: #b0b0b0;
}

// Events container
.gcal-calendar-container .fc-daygrid-day-events {
  margin-top: 2px;
  padding: 0 4px;
}

// Event styles - dot events (time + title) with highlight
.gcal-calendar-container .fc-daygrid-dot-event {
  background: rgba(26, 115, 232, 0.15) !important;
  border: none !important;
  border-left: 3px solid #1a73e8 !important;
  padding: 4px 8px 4px 6px;
  margin: 2px 4px;
  border-radius: 4px;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(26, 115, 232, 0.25) !important;
    transform: translateX(2px);
  }
}

body:not(.dark) .gcal-calendar-container .fc-daygrid-dot-event {
  background: rgba(26, 115, 232, 0.1) !important;

  &:hover {
    background: rgba(26, 115, 232, 0.2) !important;
  }
}

.gcal-calendar-container .fc-daygrid-event-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none !important;
  margin-right: 8px;
  flex-shrink: 0;
  background: #1a73e8;
}

.gcal-calendar-container .fc-event-time {
  font-size: 11px;
  font-weight: 500;
  color: #8ab4f8;
  margin-right: 6px;
}

body:not(.dark) .gcal-calendar-container .fc-event-time {
  color: #1a73e8;
}

.gcal-calendar-container .fc-event-title {
  font-size: 12px;
  font-weight: 500;
  color: #e8eaed;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

body:not(.dark) .gcal-calendar-container .fc-event-title {
  color: #3c4043;
}

// Block events (all-day events) - colored background
.gcal-calendar-container .fc-daygrid-block-event {
  border-radius: 4px !important;
  border: none !important;
  padding: 4px 10px;
  margin: 2px 4px;
  font-size: 12px;
  background: #0b8043 !important;
  transition: all 0.15s ease;

  &:hover {
    transform: translateX(2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  .fc-event-title {
    font-weight: 500;
    color: white;
  }
}

// Event colors
.gcal-calendar-container .fc-event {
  cursor: pointer;
}

// More link
.gcal-calendar-container .fc-daygrid-more-link {
  color: #8ab4f8;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 4px;
  margin-top: 2px;
}

body:not(.dark) .gcal-calendar-container .fc-daygrid-more-link {
  color: #1a73e8;
}

// Time Grid (Week/Day view)
.gcal-calendar-container .fc-timegrid-slot-label {
  font-size: 10px;
  color: #9aa0a6;
}

body:not(.dark) .gcal-calendar-container .fc-timegrid-slot-label {
  color: #70757a;
}

.gcal-calendar-container .fc-timegrid-now-indicator-line {
  border-color: #ea4335;
  border-width: 2px;
}

.gcal-calendar-container .fc-timegrid-now-indicator-arrow {
  border-color: #ea4335;
  border-top-color: transparent;
  border-bottom-color: transparent;
}

// List view
.gcal-calendar-container .fc-list-day-cushion {
  background: #3c4043;
}

body:not(.dark) .gcal-calendar-container .fc-list-day-cushion {
  background: #f1f3f4;
}

.gcal-calendar-container .fc-list-event:hover td {
  background: rgba(255, 255, 255, 0.05);
}

body:not(.dark) .gcal-calendar-container .fc-list-event:hover td {
  background: rgba(0, 0, 0, 0.05);
}

// Responsive adjustments
@media (max-width: 768px) {
  .gcal-calendar-container .fc-daygrid-day {
    min-height: 80px;
  }

  .gcal-calendar-container .fc-daygrid-day-number {
    font-size: 11px;
    width: 22px;
    height: 22px;
  }

  .gcal-calendar-container .fc-event-time,
  .gcal-calendar-container .fc-event-title {
    font-size: 11px;
  }
}

// Modal
.gcal-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.gcal-modal {
  background: $gcal-bg;
  border-radius: 8px;
  width: 450px;
  max-width: 90vw;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);

  .dark & {
    background: $gcal-bg-dark;
  }
}

.gcal-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 8px;
}

.gcal-modal-title-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 22px;
  font-weight: 400;
  color: $gcal-text;
  outline: none;

  &::placeholder {
    color: $gcal-text-secondary;
  }

  .dark & {
    color: $gcal-text-dark;

    &::placeholder {
      color: $gcal-text-secondary-dark;
    }
  }
}

.gcal-modal-close {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $gcal-text-secondary;

  &:hover {
    background: $gcal-hover;
  }

  .dark & {
    color: $gcal-text-secondary-dark;

    &:hover {
      background: $gcal-hover-dark;
    }
  }
}

.gcal-modal-body {
  padding: 8px 16px 16px;
}

.gcal-modal-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.gcal-modal-icon {
  color: $gcal-text-secondary;
  flex-shrink: 0;
  margin-top: 8px;

  .dark & {
    color: $gcal-text-secondary-dark;
  }
}

.gcal-modal-datetime {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.gcal-datetime-input {
  padding: 8px;
  border: none;
  background: $gcal-hover;
  border-radius: 4px;
  font-size: 14px;
  color: $gcal-text;
  outline: none;

  &:focus {
    background: #e8f0fe;
  }

  .dark & {
    background: $gcal-hover-dark;
    color: $gcal-text-dark;

    &:focus {
      background: #394457;
    }
  }
}

.gcal-datetime-separator {
  color: $gcal-text-secondary;

  .dark & {
    color: $gcal-text-secondary-dark;
  }
}

.gcal-modal-textarea {
  flex: 1;
  padding: 8px;
  border: none;
  background: $gcal-hover;
  border-radius: 4px;
  font-size: 14px;
  color: $gcal-text;
  outline: none;
  resize: none;
  font-family: inherit;

  &:focus {
    background: #e8f0fe;
  }

  &::placeholder {
    color: $gcal-text-secondary;
  }

  .dark & {
    background: $gcal-hover-dark;
    color: $gcal-text-dark;

    &:focus {
      background: #394457;
    }

    &::placeholder {
      color: $gcal-text-secondary-dark;
    }
  }
}

.gcal-modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid $gcal-border;

  .dark & {
    border-color: $gcal-border-dark;
  }
}

.gcal-modal-delete {
  padding: 8px 16px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #d93025;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: #fce8e6;
  }
}

.gcal-modal-actions {
  display: flex;
  gap: 8px;
}

.gcal-modal-cancel {
  padding: 8px 16px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: $gcal-primary;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: #e8f0fe;
  }
}

.gcal-modal-save {
  padding: 8px 24px;
  border: none;
  background: $gcal-primary;
  font-size: 14px;
  font-weight: 500;
  color: white;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: $gcal-primary-hover;
  }
}
</style>
